<template>
  <v-container fluid>
    <v-card :flat="true">
      <!-- パスワード変更入力フォーム -->
      <v-card v-show="!displayConfirmDialog && !displayCompletedDialog" :flat="true">
        <v-card :flat="true">
          <v-card-title class="password-change-title text-h5 font-weight-bold">
            パスワード変更
          </v-card-title>
          <v-card-text>
            <v-card :flat="true" class="pl-2 pl-sm-5 pt-3">
              <v-row dense>
                <!-- 現在のパスワード -->
                <v-col cols="12" sm="4" md="4" lg="4" class="font-weight-black">
                  <v-row align="center" dense class="form-label-row mr-3 justify-sm-end">
                    現在のパスワード
                    <ImportantChip></ImportantChip>
                  </v-row>
                </v-col>
                <v-col cols="12" sm="6" md="6" lg="6">
                  <div
                    class="password-input-group"
                    :class="{ 'password-input-group--error': currentPasswordErrors.length > 0 }"
                  >
                    <v-text-field
                      v-model="currentPassword"
                      class="password-input"
                      placeholder="現在のパスワードを入力"
                      density="compact"
                      variant="outlined"
                      :type="showCurrentPassword ? 'text' : 'password'"
                      :error-messages="currentPasswordErrors"
                      @blur="currentPasswordTouched = true"
                    />
                    <v-btn
                      class="password-toggle"
                      variant="outlined"
                      :icon="showCurrentPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      tabindex="-1"
                      @click="showCurrentPassword = !showCurrentPassword"
                    />
                  </div>
                </v-col>
              </v-row>
              <v-row dense>
                <!-- 新しいパスワード -->
                <v-col cols="12" sm="4" md="4" lg="4" class="font-weight-black">
                  <v-row align="center" dense class="form-label-row mr-3 justify-sm-end">
                    新しいパスワード
                    <v-tooltip location="top">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-help-circle-outline" />
                      </template>
                      <span>
                        8文字以上。半角英大文字・小文字・数字・記号を各1つ以上含めてください。<br />
                        使用可能な記号: ^ $ * . [ ] { } ( ) ? - " ! @ # % &amp; / \ , &gt; &lt; ' : ; | _ ~ + =<br />
                        先頭・末尾以外のスペースも記号として使用できます。
                      </span>
                    </v-tooltip>
                    <ImportantChip></ImportantChip>
                  </v-row>
                </v-col>
                <v-col cols="12" sm="6" md="6" lg="6">
                  <div
                    class="password-input-group"
                    :class="{ 'password-input-group--error': newPasswordErrors.length > 0 }"
                  >
                    <v-text-field
                      v-model="newPassword"
                      class="password-input"
                      placeholder="新しいパスワードを入力"
                      density="compact"
                      variant="outlined"
                      :type="showNewPassword ? 'text' : 'password'"
                      :error-messages="newPasswordErrors"
                      @blur="newPasswordTouched = true"
                    />
                    <v-btn
                      class="password-toggle"
                      variant="outlined"
                      :icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      tabindex="-1"
                      @click="showNewPassword = !showNewPassword"
                    />
                  </div>
                </v-col>
              </v-row>
              <v-row dense>
                <!-- 新しいパスワード（確認用） -->
                <v-col cols="12" sm="4" md="4" lg="4" class="font-weight-black">
                  <v-row align="center" dense class="form-label-row mr-3 justify-sm-end">
                    新しいパスワード（確認）
                    <ImportantChip></ImportantChip>
                  </v-row>
                </v-col>
                <v-col cols="12" sm="6" md="6" lg="6">
                  <div
                    class="password-input-group"
                    :class="{ 'password-input-group--error': confirmNewPasswordErrors.length > 0 }"
                  >
                    <v-text-field
                      v-model="confirmNewPassword"
                      class="password-input"
                      placeholder="新しいパスワードを再入力"
                      density="compact"
                      variant="outlined"
                      :type="showConfirmNewPassword ? 'text' : 'password'"
                      :error-messages="confirmNewPasswordErrors"
                      @blur="confirmNewPasswordTouched = true"
                    />
                    <v-btn
                      class="password-toggle"
                      variant="outlined"
                      :icon="showConfirmNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      tabindex="-1"
                      @click="showConfirmNewPassword = !showConfirmNewPassword"
                    />
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-card-text>
          <v-card-actions class="justify-center py-10">
            <v-btn
              variant="flat"
              color="primary"
              class="px-16"
              :disabled="!isFormValid"
              @click="onClickOpenConfirmDialog"
            >
              <v-icon>mdi-lock-reset</v-icon>
              変更手続きへ(確認)
            </v-btn>
          </v-card-actions>
        </v-card>
        <v-overlay
          :model-value="overlay"
          class="d-flex align-center justify-center"
        >
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </v-card>
      <!-- パスワード変更確認ダイアログ { -->
      <v-card v-if="displayConfirmDialog" :flat="true">
        <v-card :flat="true">
          <v-card-title class="password-change-title text-h5 font-weight-bold">
            パスワード変更確認
          </v-card-title>
          <v-card-text class="pt-5">
            <v-row class="mb-3">
              <v-col cols="12" class="text-body-1 text-center">
                パスワードを変更します。よろしいですか？
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="confirm-actions justify-center py-10">
            <v-btn
              variant="flat"
              color="return"
              class="confirm-actions__button px-16"
              @click="onClickConfirmCancel"
            >
              <v-icon>mdi-arrow-left</v-icon>
              戻る
            </v-btn>
            <v-btn
              variant="flat"
              color="primary"
              class="confirm-actions__button px-16"
              @click="onClickConfirmEntry"
            >
              <v-icon>mdi-check</v-icon>
              変更する
            </v-btn>
          </v-card-actions>
          <v-overlay
            :model-value="overlay"
            class="d-flex align-center justify-center"
          >
            <v-progress-circular indeterminate size="64"></v-progress-circular>
          </v-overlay>
        </v-card>
      </v-card>
      <!-- } パスワード変更確認ダイアログ -->
      <!-- パスワード変更完了ダイアログ { -->
      <v-card v-if="displayCompletedDialog" :flat="true">
        <v-card :flat="true">
          <v-card-title class="password-change-title text-h5 font-weight-bold">
            パスワード変更完了
          </v-card-title>
          <v-card-text class="pt-5">
            <v-row class="mb-3">
              <v-col cols="12" class="text-body-1 text-center">
                パスワードの変更が完了しました。
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="justify-center py-10">
            <v-btn
              variant="flat"
              color="primary"
              class="px-16"
              @click="onClickCompletedClose"
            >
              OK
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-card>
      <!-- } パスワード変更完了ダイアログ -->
      <!-- { エラーダイアログ -->
      <AlertDialog
        v-model="errdialog"
        title="エラー"
        :message="errMessage"
        type="error"
      />
      <!-- } エラーダイアログ -->
    </v-card>
  </v-container>
</template>

<script setup lang="ts">
import { ref } from "vue";

import AlertDialog from "@/components/AlertDialog.vue";
import ImportantChip from "@/components/ImportantChip.vue";

import { usePasswordChangeForm } from "./composables/usePasswordChangeForm";
import { usePasswordChangeActions } from "./composables/usePasswordChangeActions";

////////////////////////////////////////////////////////////////////////////////
// UI状態
////////////////////////////////////////////////////////////////////////////////
const errdialog = ref<boolean>(false);
const errMessage = ref<string>("");
const overlay = ref<boolean>(false);
const displayConfirmDialog = ref<boolean>(false);
const displayCompletedDialog = ref<boolean>(false);

/** パスワードマスク表示切替 */
const showCurrentPassword = ref<boolean>(false);
const showNewPassword = ref<boolean>(false);
const showConfirmNewPassword = ref<boolean>(false);

////////////////////////////////////////////////////////////////////////////////
// Composables
////////////////////////////////////////////////////////////////////////////////
const {
  currentPassword,
  newPassword,
  confirmNewPassword,
  currentPasswordTouched,
  newPasswordTouched,
  confirmNewPasswordTouched,
  currentPasswordErrors,
  newPasswordErrors,
  confirmNewPasswordErrors,
  isFormValid,
  touchAll,
  validate,
  clearForm,
} = usePasswordChangeForm();

const {
  onClickOpenConfirmDialog,
  onClickConfirmCancel,
  onClickConfirmEntry,
  onClickCompletedClose,
} = usePasswordChangeActions({
  errdialog,
  errMessage,
  overlay,
  displayConfirmDialog,
  displayCompletedDialog,
  currentPassword,
  newPassword,
  confirmNewPassword,
  validate,
  clearForm,
});
</script>

<style scoped>
.password-change-title {
  margin-bottom: 12px;
  padding-top: 16px;
  padding-bottom: 20px;
}

.password-input-group {
  display: flex;
  align-items: stretch;
  width: 100%;
}

@media (min-width: 600px) {
  .password-input-group {
    width: 60%;
  }
}

.password-input {
  flex: 1 1 auto;
  min-width: 0;
}

.password-input :deep(.v-input__control) {
  min-height: 40px;
}

.password-toggle {
  --v-btn-height: 40px;
  min-width: 48px;
  width: 48px;
  height: 40px;
  max-height: 40px;
  min-height: 40px;
  padding: 0;
  align-self: flex-start;
  color: #3e3e3e;
  background-color: rgb(var(--v-theme-surface));
  border: none;
  box-shadow: inset 0 1px 0 #b6b6b6, inset -1px 0 0 #b6b6b6, inset 0 -1px 0 #b6b6b6;
  border-radius: 0 4px 4px 0;
}

.password-toggle :deep(.v-btn__overlay),
.password-toggle :deep(.v-btn__underlay),
.password-toggle :deep(.v-btn__content) {
  border-radius: 0 4px 4px 0;
}

.password-toggle :deep(.v-icon) {
  color: #3e3e3e;
  opacity: 1;
  font-size: 18px;
}

.password-toggle:hover {
  background-color: #e9f9fc;
  box-shadow: inset 0 1px 0 #40aabf, inset -1px 0 0 #40aabf, inset 0 -1px 0 #40aabf;
}

.password-input :deep(.v-field) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

@media (max-width: 599px) {
  .confirm-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .confirm-actions__button {
    width: 100%;
    margin-inline: 0;
  }
}
</style>
